import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import {
  MagnifyingGlass,
  DownloadSimple,
  SignOut,
  CaretUp,
  CaretDown,
  Pencil,
  Trash,
  CircleNotch,
  CaretLeft,
  CaretRight,
  Star,
  ArrowsOutLineHorizontal,
  ArrowsClockwise,
  Calendar,
} from '@phosphor-icons/react';
import WindowsNavMenu from '../components/dashboard/WindowsNavMenu';
import AddDrawDialog from '../components/dashboard/AddDrawDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

// Default column widths
const DEFAULT_COLUMN_WIDTHS = {
  draw_number: 100,
  draw_date: 120,
  day: 60,
  month: 70,
  year: 70,
  main_balls: 200,
  lucky_stars: 100,
  actions: 100,
};

// Ball component
const Ball = ({ number, type = 'main' }) => (
  <span
    className={type === 'main' ? 'ball-main' : 'ball-lucky'}
    data-testid={`ball-${type}-${number}`}
  >
    {number}
  </span>
);

// Stats Card
const StatsCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 rounded p-4">
    <p className="text-xs uppercase tracking-wider font-medium text-gray-500">{label}</p>
    <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
      {value || '-'}
    </p>
  </div>
);

// Resizable Column Header
const ResizableHeader = ({ children, width, onResize, columnKey, onClick, className = '' }) => {
  const headerRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    startXRef.current = e.clientX;
    startWidthRef.current = width;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    const diff = e.clientX - startXRef.current;
    const newWidth = Math.max(50, startWidthRef.current + diff);
    onResize(columnKey, newWidth);
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  return (
    <th
      ref={headerRef}
      className={`relative ${className}`}
      style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }}
      onClick={onClick}
    >
      <div className="flex items-center gap-1 pr-3">
        {children}
      </div>
      <div
        className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center"
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
        data-testid={`resize-${columnKey}`}
      >
        <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
      </div>
    </th>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [draws, setDraws] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('draw_number');
  const [sortOrder, setSortOrder] = useState('desc');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDraw, setEditingDraw] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [calendarStrength, setCalendarStrength] = useState(null);

  // Column widths state - load from localStorage
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = localStorage.getItem('euromillions_column_widths');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMN_WIDTHS;
  });

  // Save column widths to localStorage
  useEffect(() => {
    localStorage.setItem('euromillions_column_widths', JSON.stringify(columnWidths));
  }, [columnWidths]);

  const handleColumnResize = (columnKey, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: newWidth
    }));
  };

  const resetColumnWidths = () => {
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
    toast.success('Column widths reset to default');
  };

  // Form state
  const [formData, setFormData] = useState({
    draw_number: '',
    draw_date: '',
    ball_1: '',
    ball_2: '',
    ball_3: '',
    ball_4: '',
    ball_5: '',
    lucky_star_1: '',
    lucky_star_2: '',
    winner_amount: '',
  });

  const fetchDraws = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/draws`, {
        params: { page, limit: 15, search, sort_by: sortBy, sort_order: sortOrder },
        withCredentials: true,
      });
      setDraws(response.data.draws);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to fetch draws');
    } finally {
      setLoading(false);
    }
  }, [page, search, sortBy, sortOrder]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/draws/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchDraws();
    fetchStats();
    // Fetch calendar pattern strength for next draw
    axios.get(`${API}/analysis/calendar-patterns`).then(res => {
      setCalendarStrength(res.data);
    }).catch(() => {});
  }, [fetchDraws]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? <CaretUp size={14} /> : <CaretDown size={14} />;
  };

  const resetForm = () => {
    setFormData({
      draw_number: '',
      draw_date: '',
      ball_1: '',
      ball_2: '',
      ball_3: '',
      ball_4: '',
      ball_5: '',
      lucky_star_1: '',
      lucky_star_2: '',
      winner_amount: '',
    });
    setEditingDraw(null);
  };

  const openEditDialog = (draw) => {
    setEditingDraw(draw);
    setFormData({
      draw_number: draw.draw_number.toString(),
      draw_date: draw.draw_date,
      ball_1: draw.ball_1.toString(),
      ball_2: draw.ball_2.toString(),
      ball_3: draw.ball_3.toString(),
      ball_4: draw.ball_4.toString(),
      ball_5: draw.ball_5.toString(),
      lucky_star_1: draw.lucky_star_1.toString(),
      lucky_star_2: draw.lucky_star_2.toString(),
      winner_amount: '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const payload = {
      draw_number: parseInt(formData.draw_number),
      draw_date: formData.draw_date,
      ball_1: parseInt(formData.ball_1),
      ball_2: parseInt(formData.ball_2),
      ball_3: parseInt(formData.ball_3),
      ball_4: parseInt(formData.ball_4),
      ball_5: parseInt(formData.ball_5),
      lucky_star_1: parseInt(formData.lucky_star_1),
      lucky_star_2: parseInt(formData.lucky_star_2),
    };

    // Winner amount for winner_draws collection
    const winnerAmt = formData.winner_amount ? formData.winner_amount.replace(/[£,\s]/g, '') : '';

    try {
      if (editingDraw) {
        await axios.put(`${API}/draws/${editingDraw.id}`, payload, { withCredentials: true });
        toast.success('Draw updated successfully');
      } else {
        await axios.post(`${API}/draws`, payload, { withCredentials: true });
        toast.success('Draw added successfully');
      }

      // Also upsert into winner_draws collection
      try {
        await axios.post(`${API}/analysis/winner-draws/sync`, {
          ...payload,
          winner_amount: winnerAmt ? parseFloat(winnerAmt) : null,
        });
        if (winnerAmt) toast.success(`Winner data updated: £${parseFloat(winnerAmt).toLocaleString()}`);
      } catch { /* silent - winner sync is best-effort */ }
      setDialogOpen(false);
      resetForm();
      fetchDraws();
      fetchStats();
      // Broadcast so other open windows/pages can refresh instantly (same-tab + cross-tab)
      try { window.dispatchEvent(new CustomEvent('euromillions:draws-updated')); } catch (_e) { /* best-effort broadcast */ }
      try { localStorage.setItem('euromillions:last_draw_update', String(Date.now())); } catch (_e) { /* best-effort broadcast */ }
      try { if (typeof BroadcastChannel !== 'undefined') { const bc = new BroadcastChannel('euromillions'); bc.postMessage('draws-updated'); bc.close(); } } catch (_e) { /* best-effort broadcast */ }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg).join(', '));
      } else {
        toast.error('Failed to save draw');
      }
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (drawId) => {
    if (!window.confirm('Are you sure you want to delete this draw?')) return;

    try {
      await axios.delete(`${API}/draws/${drawId}`, { withCredentials: true });
      toast.success('Draw deleted successfully');
      fetchDraws();
      fetchStats();
      try { window.dispatchEvent(new CustomEvent('euromillions:draws-updated')); } catch (_e) { /* best-effort broadcast */ }
      try { localStorage.setItem('euromillions:last_draw_update', String(Date.now())); } catch (_e) { /* best-effort broadcast */ }
      try { if (typeof BroadcastChannel !== 'undefined') { const bc = new BroadcastChannel('euromillions'); bc.postMessage('draws-updated'); bc.close(); } } catch (_e) { /* best-effort broadcast */ }
    } catch (error) {
      toast.error('Failed to delete draw');
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/draws/export`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `euromillions_draws_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const handleAutoSync = async () => {
    setSyncLoading(true);
    // Warm the backend first — the Auto-Sync endpoint does external I/O
    // (scraping euro-millions.com) so a cold Python worker + slow scrape can
    // easily exceed Cloudflare's proxy timeout on the first hit and surface
    // as a 5xx "origin overloaded" error. A cheap /api/health ping wakes the
    // pod so the real POST lands on a warm worker.
    try {
      await axios.get(`${API}/health`, { withCredentials: true, timeout: 25000 });
    } catch (_) { /* ignore — the real POST below handles hard failures */ }

    const MAX_ATTEMPTS = 3;
    let attempt = 0;
    let lastError = null;
    while (attempt < MAX_ATTEMPTS) {
      attempt += 1;
      try {
        const res = await axios.post(`${API}/draws/auto-sync`, {}, {
          withCredentials: true,
          timeout: 60000, // 60s: plenty for a 1-2 year scrape
        });
        const { inserted, message, draws: newDraws } = res.data;
        if (inserted > 0) {
          toast.success(`${message} — latest: #${res.data.latest_draw_number} (${res.data.latest_draw_date})`);
          if (newDraws?.length) {
            toast.info(`New: ${newDraws.map(d => d.draw_date).join(', ')}`);
          }
          fetchDraws();
          // Keep already-open Diagnostics1 tabs in sync after automatic ingestion.
          try { window.dispatchEvent(new CustomEvent('euromillions:draws-updated')); } catch (_e) { /* best-effort broadcast */ }
          try { localStorage.setItem('euromillions:last_draw_update', String(Date.now())); } catch (_e) { /* best-effort broadcast */ }
          try { if (typeof BroadcastChannel !== 'undefined') { const bc = new BroadcastChannel('euromillions'); bc.postMessage('draws-updated'); bc.close(); } } catch (_e) { /* best-effort broadcast */ }
        } else {
          toast.info(message);
        }
        setSyncLoading(false);
        return;
      } catch (error) {
        lastError = error;
        const status = error.response?.status;
        // Retry only on 5xx / network / timeout (cold-start pattern). Auth or
        // 4xx errors should surface immediately.
        const retryable = !status || status >= 500 || error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
        if (!retryable || attempt >= MAX_ATTEMPTS) break;
        toast.info(`Backend warming up… retrying (${attempt}/${MAX_ATTEMPTS - 1})`);
        await new Promise((r) => setTimeout(r, 3000 * attempt));
      }
    }

    const detail = lastError?.response?.data?.detail;
    if (typeof detail === 'string') {
      toast.error(detail);
    } else if (lastError?.response?.status >= 500 || !lastError?.response) {
      toast.error('Server is temporarily unreachable. Please try again in a moment.');
    } else {
      toast.error('Auto-sync failed — please retry');
    }
    setSyncLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors toastOptions={{ style: { zIndex: 99999 } }} className="z-[99999]" />

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center">
                <Star size={24} weight="fill" className="text-amber-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>
                  Ashish Analytical Application
                </h1>
                <p className="text-xs text-gray-500">Window 1: Raw Draw Data</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Windows Navigation */}
              <WindowsNavMenu />

              {user && (
                <>
                  <span className="text-sm text-gray-500">{user.email}</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    className="text-gray-500 hover:text-gray-700"
                    data-testid="logout-btn"
                  >
                    <SignOut size={18} />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatsCard label="Total Draws" value={stats.total_draws} />
          <StatsCard label="Latest Draw" value={stats.latest_draw} />
          <StatsCard label="Latest Date" value={stats.latest_date} />
          <StatsCard label="Oldest Draw" value={stats.oldest_draw} />
        </div>

        {/* Calendar Pattern Strength for Next Draw */}
        {calendarStrength?.pattern_strength && (
          <div
            className="mb-6 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-4 text-white cursor-pointer hover:from-indigo-700 hover:to-violet-700 transition-all"
            onClick={() => navigate('/window17')}
            data-testid="calendar-strength-card"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar size={20} weight="fill" />
                  <div>
                    <div className="text-sm font-bold">Next Draw: {calendarStrength.month_name} {calendarStrength.day}</div>
                    <div className="text-xs text-indigo-200">{calendarStrength.matched_count} historical draws on this date</div>
                  </div>
                </div>
                <div className="hidden sm:flex gap-2">
                  {calendarStrength.signals?.filter(s => s.strength === "HIGH").slice(0, 3).map((sig, i) => (
                    <span key={i} className="bg-white/15 backdrop-blur rounded-full px-2 py-0.5 text-[10px] font-medium">{sig.label}</span>
                  ))}
                  {(calendarStrength.signals?.filter(s => s.strength === "HIGH").length || 0) > 3 && (
                    <span className="bg-white/15 backdrop-blur rounded-full px-2 py-0.5 text-[10px] font-medium">
                      +{calendarStrength.signals.filter(s => s.strength === "HIGH").length - 3} more
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className={`text-3xl font-black ${calendarStrength.pattern_strength.score >= 75 ? 'text-emerald-300' : calendarStrength.pattern_strength.score >= 50 ? 'text-yellow-300' : calendarStrength.pattern_strength.score >= 30 ? 'text-orange-300' : 'text-red-300'}`}>
                  {calendarStrength.pattern_strength.score}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-200">{calendarStrength.pattern_strength.label}</div>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white border border-gray-200 rounded p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by number, date, or ball..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-10 form-input"
                  data-testid="search-input"
                />
              </div>
              <Select value={`${sortBy}-${sortOrder}`} onValueChange={(value) => {
                const [col, order] = value.split('-');
                setSortBy(col);
                setSortOrder(order);
                setPage(1);
              }}>
                <SelectTrigger className="w-48" data-testid="sort-select">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draw_number-desc">Draw # (Newest)</SelectItem>
                  <SelectItem value="draw_number-asc">Draw # (Oldest)</SelectItem>
                  <SelectItem value="draw_date-desc">Date (Newest)</SelectItem>
                  <SelectItem value="draw_date-asc">Date (Oldest)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                onClick={resetColumnWidths}
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
                title="Reset column widths"
                data-testid="reset-columns-btn"
              >
                <ArrowsOutLineHorizontal size={18} />
              </Button>
              <Button
                onClick={handleExport}
                disabled={exportLoading}
                className="btn-secondary flex items-center gap-2"
                data-testid="export-btn"
                title="Download all draws as Excel (.xlsx)"
              >
                {exportLoading ? (
                  <CircleNotch size={18} className="animate-spin" />
                ) : (
                  <DownloadSimple size={18} />
                )}
                Excel Export
              </Button>

              {user && (
                <Button
                  onClick={handleAutoSync}
                  disabled={syncLoading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2"
                  title="Fetch latest official EuroMillions results and add any missing draws"
                  data-testid="auto-sync-btn"
                >
                  {syncLoading ? (
                    <CircleNotch size={18} className="animate-spin" />
                  ) : (
                    <ArrowsClockwise size={18} />
                  )}
                  Auto-Sync
                </Button>
              )}

              {user && (
                <AddDrawDialog
                  dialogOpen={dialogOpen}
                  setDialogOpen={setDialogOpen}
                  editingDraw={editingDraw}
                  draws={draws}
                  formData={formData}
                  setFormData={setFormData}
                  formLoading={formLoading}
                  handleSubmit={handleSubmit}
                  resetForm={resetForm}
                />
              )}
            </div>
          </div>
        </div>

        {/* Resize instruction */}
        <div className="text-xs text-gray-400 mb-2 flex items-center gap-1">
          <ArrowsOutLineHorizontal size={14} />
          <span>Drag column borders to resize (like Excel)</span>
        </div>

        {/* Table */}
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table" style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }} data-testid="draws-table">
              <thead>
                <tr>
                  <ResizableHeader
                    width={columnWidths.draw_number}
                    onResize={handleColumnResize}
                    columnKey="draw_number"
                    onClick={() => handleSort('draw_number')}
                    className="cursor-pointer hover:bg-gray-100"
                  >
                    Draw # {renderSortIcon("draw_number")}
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.draw_date}
                    onResize={handleColumnResize}
                    columnKey="draw_date"
                    onClick={() => handleSort('draw_date')}
                    className="cursor-pointer hover:bg-gray-100"
                  >
                    Date {renderSortIcon("draw_date")}
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.day}
                    onResize={handleColumnResize}
                    columnKey="day"
                  >
                    Day
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.month}
                    onResize={handleColumnResize}
                    columnKey="month"
                  >
                    Month
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.year}
                    onResize={handleColumnResize}
                    columnKey="year"
                  >
                    Year
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.main_balls}
                    onResize={handleColumnResize}
                    columnKey="main_balls"
                    className="text-center"
                  >
                    Main Balls
                  </ResizableHeader>
                  <ResizableHeader
                    width={columnWidths.lucky_stars}
                    onResize={handleColumnResize}
                    columnKey="lucky_stars"
                    className="text-center"
                  >
                    Lucky Stars
                  </ResizableHeader>
                  {user && (
                    <ResizableHeader
                      width={columnWidths.actions}
                      onResize={handleColumnResize}
                      columnKey="actions"
                      className="text-right"
                    >
                      Actions
                    </ResizableHeader>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={user ? 8 : 7} className="text-center py-8">
                      <CircleNotch size={24} className="animate-spin mx-auto text-gray-400" />
                    </td>
                  </tr>
                ) : draws.length === 0 ? (
                  <tr>
                    <td colSpan={user ? 8 : 7} className="text-center py-8 text-gray-500">
                      No draws found. {user ? 'Add your first draw!' : 'Login to add draws.'}
                    </td>
                  </tr>
                ) : (
                  draws.map((draw) => (
                    <tr key={draw.id} data-testid={`draw-row-${draw.draw_number}`}>
                      <td className="font-mono font-bold" style={{ width: columnWidths.draw_number }}>{draw.draw_number}</td>
                      <td style={{ width: columnWidths.draw_date }}>{draw.draw_date}</td>
                      <td className="text-gray-500" style={{ width: columnWidths.day }}>{draw.day}</td>
                      <td className="text-gray-500" style={{ width: columnWidths.month }}>{draw.month}</td>
                      <td className="text-gray-500" style={{ width: columnWidths.year }}>{draw.year}</td>
                      <td style={{ width: columnWidths.main_balls }}>
                        <div className="flex items-center justify-center gap-1 flex-wrap">
                          <Ball number={draw.ball_1} type="main" />
                          <Ball number={draw.ball_2} type="main" />
                          <Ball number={draw.ball_3} type="main" />
                          <Ball number={draw.ball_4} type="main" />
                          <Ball number={draw.ball_5} type="main" />
                        </div>
                      </td>
                      <td style={{ width: columnWidths.lucky_stars }}>
                        <div className="flex items-center justify-center gap-1">
                          <Ball number={draw.lucky_star_1} type="lucky" />
                          <Ball number={draw.lucky_star_2} type="lucky" />
                        </div>
                      </td>
                      {user && (
                        <td style={{ width: columnWidths.actions }}>
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(draw)}
                              className="text-gray-500 hover:text-[#002FA7]"
                              data-testid={`edit-draw-${draw.draw_number}`}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(draw.id)}
                              className="text-gray-500 hover:text-red-600"
                              data-testid={`delete-draw-${draw.draw_number}`}
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="btn-secondary"
                  data-testid="prev-page-btn"
                >
                  <CaretLeft size={16} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="btn-secondary"
                  data-testid="next-page-btn"
                >
                  <CaretRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Login prompt for guests */}
        {!user && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded p-4 text-center">
            <p className="text-sm text-blue-800">
              <a href="/login" className="font-medium underline hover:text-blue-900">Login</a> to add, edit, or delete draws.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
